import axios from '../axiosConfig'
import type { CodeItemGroup, UnselectableValuesReport } from '../types/codeIndex'
import {
  buildBranchTreeUpdateRequest,
  mapBranchTreeToCodeItemGroups,
  type BranchTreeUpdateRequest,
} from '../utils/projectBranchMapper'

export type ProjectStatus =
  | 'InitialSetup'
  | 'Open'
  | 'SubmittedForReview'
  | 'AllApproved'
  | 'Merged'
  | 'Unpublished'
  | 'Published'
  | 'Cancelled'
  | 'OnHold'
  | string

export interface ProjectUser {
  id?: string | null
  userId?: string | null
  name?: string | null
  emailAddress?: string | null
  avatarUri?: string | null
}

export interface ProjectBranch {
  id: string
  name: string
  description?: string | null
  targetRevision?: string | null
  changeEcoNumber?: string | null
  changes?: {
    approved: number
    pending: number
    rejected: number
  } | null
  status: ProjectStatus
  merged?: string | null
  published?: string | null
  cancelled?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  createdBy?: ProjectUser | null
  // Latest unselectable-values check stored on the project; absent on main.
  unselectableValues?: UnselectableValuesReport | null
}

export interface ProjectCancellationPreview {
  projectId: string
  projectName: string
  currentStatus: ProjectStatus
  changeCount: number
  pendingReviewCount: number
  canCancel: boolean
}

export interface CreateProjectBranchRequest {
  name: string
  description?: string | null
  targetRevision?: string | null
  changeEcoNumber?: string | null
  fromVersionId?: string | null
  fromProjectId?: string | null
}

export interface BranchTreeNode {
  id?: string | null
  elementId: string
  nodeType: string
  properties: Record<string, string | null | undefined>
}

export interface BranchTreeEdge {
  parentElementId: string
  childElementId: string
  relType: string
}

export interface BranchTree {
  projectId: string
  rootNodeIds: string[]
  nodes: BranchTreeNode[]
  edges: BranchTreeEdge[]
  // Re-run of the unselectable-values check after a tree update.
  unselectableValues?: UnselectableValuesReport | null
}

export interface ProjectBranchSnapshot {
  project: ProjectBranch
  tree: BranchTree
  groups: CodeItemGroup[]
}

export interface ProjectArrivedChange {
  nodeId: string
  nodeType: string
  operation: 'Create' | 'Update' | 'Delete' | string
  path?: string | null
  revision?: string | null
  mergedAt?: string | null
}

export interface ProjectMergeConflict {
  nodeId: string
  nodeType?: string | null
  codeItemName?: string | null
  path?: string | null
  reason?: string | null
  projectValue?: string | null
  liveValue?: string | null
  canKeepProject?: boolean
}

export interface ProjectComparison {
  projectId: string
  baseTree: BranchTree
  liveTree: BranchTree
  reviewBaseTree?: BranchTree | null
  arrivedChanges: ProjectArrivedChange[]
  conflicts: {
    hasConflicts: boolean
    target: string
    conflicts: ProjectMergeConflict[]
    checkedAt?: string | null
  }
}

function branchPath(codeIndexId: string): string {
  return `/codeindex/${encodeURIComponent(codeIndexId)}/branches`
}

export async function fetchProjectBranches(codeIndexId: string): Promise<ProjectBranch[]> {
  const { data } = await axios.get<ProjectBranch[]>(branchPath(codeIndexId))
  return Array.isArray(data) ? data : []
}

export async function fetchProjectBranch(codeIndexId: string, projectId: string): Promise<ProjectBranch> {
  const { data } = await axios.get<ProjectBranch>(
    `${branchPath(codeIndexId)}/${encodeURIComponent(projectId)}`
  )
  return data
}

export async function createProjectBranch(
  codeIndexId: string,
  payload: CreateProjectBranchRequest
): Promise<ProjectBranch> {
  const { data } = await axios.post<ProjectBranch>(branchPath(codeIndexId), payload, {
    headers: { 'Content-Type': 'application/json' },
  })
  return data
}

export async function fetchProjectBranchTree(codeIndexId: string, projectId: string): Promise<BranchTree> {
  const { data } = await axios.get<BranchTree>(
    `${branchPath(codeIndexId)}/${encodeURIComponent(projectId)}/tree`
  )
  return {
    projectId: data?.projectId || projectId,
    rootNodeIds: Array.isArray(data?.rootNodeIds) ? data.rootNodeIds : [],
    nodes: Array.isArray(data?.nodes) ? data.nodes : [],
    edges: Array.isArray(data?.edges) ? data.edges : [],
  }
}

export async function fetchProjectComparison(codeIndexId: string, projectId: string): Promise<ProjectComparison> {
  const { data } = await axios.get<ProjectComparison>(
    `${branchPath(codeIndexId)}/${encodeURIComponent(projectId)}/comparison`
  )
  return {
    projectId: data.projectId || projectId,
    baseTree: data.baseTree,
    liveTree: data.liveTree,
    reviewBaseTree: data.reviewBaseTree,
    arrivedChanges: Array.isArray(data.arrivedChanges) ? data.arrivedChanges : [],
    conflicts: data.conflicts || { hasConflicts: false, target: 'main', conflicts: [] },
  }
}

export async function updateProjectBranchTree(
  codeIndexId: string,
  projectId: string,
  payload: BranchTreeUpdateRequest
): Promise<BranchTree> {
  const { data } = await axios.put<BranchTree>(
    `${branchPath(codeIndexId)}/${encodeURIComponent(projectId)}/tree`,
    payload,
    { headers: { 'Content-Type': 'application/json' } }
  )
  return data
}

export async function fetchProjectCancellationPreview(
  codeIndexId: string,
  projectId: string
): Promise<ProjectCancellationPreview> {
  const { data } = await axios.get<ProjectCancellationPreview>(
    `${branchPath(codeIndexId)}/${encodeURIComponent(projectId)}/cancel-preview`
  )
  return data
}

export async function cancelProject(
  codeIndexId: string,
  projectId: string,
  acknowledgedChangeCount: number
): Promise<ProjectBranch> {
  const { data } = await axios.post<ProjectBranch>(
    `${branchPath(codeIndexId)}/${encodeURIComponent(projectId)}/cancel`,
    { confirmed: true, acknowledgedChangeCount },
    { headers: { 'Content-Type': 'application/json' } }
  )
  return data
}

export async function fetchProjectBranchSnapshot(
  codeIndexId: string,
  projectId: string
): Promise<ProjectBranchSnapshot> {
  const [project, tree] = await Promise.all([
    fetchProjectBranch(codeIndexId, projectId),
    fetchProjectBranchTree(codeIndexId, projectId),
  ])

  return {
    project,
    tree,
    groups: mapBranchTreeToCodeItemGroups(tree),
  }
}

export async function saveProjectBranchGroups(
  codeIndexId: string,
  projectId: string,
  originalGroups: CodeItemGroup[],
  currentGroups: CodeItemGroup[]
): Promise<BranchTree> {
  return updateProjectBranchTree(
    codeIndexId,
    projectId,
    buildBranchTreeUpdateRequest(originalGroups, currentGroups)
  )
}
